import type { MouseEvent, ReactNode, Ref } from 'react';
import classNames from 'classnames';

import { analytics } from '@/analytics/core/analytics';
import { resolveButtonTracking } from '@/analytics/core/resolveButtonTracking';

import ButtonContent from './ButtonContent';
import type { ButtonBaseProps, ButtonLabelProps, ButtonProps, NativeButtonProps } from './types';

import styles from './button.module.scss';

function tooltipText(tooltip: ReactNode): string | undefined {
    return typeof tooltip === 'string' ? tooltip : undefined;
}

function trackClick(props: ButtonProps): void {
    const tracking = resolveButtonTracking({
        trackingId: props.trackingId,
        tooltip: tooltipText(props.tooltip),
        name: props.name,
    });
    if (tracking) {
        analytics.buttonClicked?.({ ...tracking, buttonVariant: props.variant ?? 'primary' });
    }
}

export default function NativeButton(
    props: ButtonBaseProps & ButtonLabelProps & NativeButtonProps & { ref?: Ref<HTMLButtonElement> }
) {
    const {
        ref,
        type = 'button',
        variant = 'primary',
        size = 'medium',
        disabled = false,
        iconOnly = false,
        stopPropagation = false,
        onClick,
    } = props;

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (stopPropagation) {
            event.stopPropagation();
        }
        trackClick(props);
        onClick?.(event);
    };

    return (
        <button
            ref={ref}
            id={props.id}
            name={props.name}
            title={props.title}
            tabIndex={props.tabIndex}
            type={type}
            form={props.form}
            value={props.value}
            disabled={disabled}
            className={classNames(
                styles.button,
                { [styles.fullWidth]: props.fullWidth },
                props.className
            )}
            data-variant={variant}
            data-size={size}
            data-icon-only={iconOnly}
            data-wrap={props.wrap || undefined}
            data-testid={props['data-testid']}
            aria-label={props['aria-label']}
            aria-describedby={props['aria-describedby']}
            aria-controls={props['aria-controls']}
            aria-expanded={props['aria-expanded']}
            aria-haspopup={props['aria-haspopup']}
            aria-current={props['aria-current']}
            aria-pressed={props.pressed}
            aria-disabled={props['aria-disabled']}
            onClick={handleClick}
            onMouseDown={props.onMouseDown}
            onPointerDown={props.onPointerDown}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
            onKeyDown={props.onKeyDown}
            onFocus={props.onFocus}
            onBlur={props.onBlur}>
            <ButtonContent icon={props.icon} iconEnd={props.iconEnd}>
                {props.children}
            </ButtonContent>
        </button>
    );
}
